import { type FC } from "react";

import { AddressInput } from "@/shared/components/ui/address-input";
import { FieldError } from "@/shared/components/ui/FieldError";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { US_STATES } from "@/shared/constants/constants";
import { extractAddressFromPlace, toStateAbbreviation } from "@/shared/utils/addressUtils";
import { cn } from "@/shared/utils/core";
import type { AddressParts } from "@/shared/utils/addressUtils";

interface AddressPickerProps {
  id?: string;
  value: AddressParts;
  onChange: (value: AddressParts) => void;
  onAutoComplete?: () => void;
  errors?: Partial<Record<keyof AddressParts, string>>;
  errorClassName?: string;
  hideErrorMessages?: boolean;
  labels?: {
    streetAddress?: string;
    apartment?: string;
  };
  stateFormat?: "label" | "abbreviation";
  /** Use equal-width columns instead of the default weighted ratios */
  equalColumns?: boolean;
  showRequired?: boolean;
  className?: string;
  labelClassName?: string;
  portalContainer?: HTMLElement | null;
}

export const AddressPicker: FC<AddressPickerProps> = ({
  id,
  value,
  onChange,
  onAutoComplete,
  errors,
  errorClassName,
  hideErrorMessages,
  labels,
  stateFormat = "label",
  equalColumns = false,
  showRequired = false,
  className,
  labelClassName,
  portalContainer,
}) => {
  const getStateOption = (s: { value: string; label: string }) =>
    stateFormat === "abbreviation" ? s.value : s.label;

  const update = (field: keyof AddressParts, fieldValue: string) => {
    onChange({ ...value, [field]: fieldValue });
  };

  const handleAddressSelect = (
    rawValue: string,
    placeDetails?: google.maps.places.Place | null,
    subpremise?: string | null,
  ) => {
    const extracted = extractAddressFromPlace(rawValue, placeDetails, subpremise);
    const extractedState = extracted.state
      ? stateFormat === "abbreviation"
        ? toStateAbbreviation(extracted.state)
        : extracted.state
      : value.state;
    onChange({
      streetAddress: extracted.streetAddress,
      apartment: extracted.apartment || value.apartment,
      city: extracted.city || value.city,
      state: extractedState,
      zipCode: extracted.zipCode || value.zipCode,
    });
    onAutoComplete?.();
  };

  const defaultErrorClass = "border-destructive border-[2px]";
  const errorClass = (field: keyof AddressParts) =>
    errors?.[field] ? (errorClassName ?? defaultErrorClass) : "";

  return (
    <div className={cn("flex flex-col gap-6", className)}>
      <div
        className={cn(
          "grid grid-cols-1 gap-x-4 gap-y-6",
          equalColumns ? "sm:grid-cols-2" : "sm:grid-cols-[1fr_0.8fr]",
        )}
      >
        <div className="flex flex-col gap-1.5">
          <Label className={labelClassName} required={showRequired} htmlFor={id}>
            {labels?.streetAddress ?? "Street address"}
          </Label>
          <AddressInput
            id={id}
            value={value.streetAddress}
            onChange={handleAddressSelect}
            placeholder="Street address"
            className={errorClass("streetAddress")}
            portalContainer={portalContainer}
          />
          {!hideErrorMessages && <FieldError message={errors?.streetAddress} className="mt-1" />}
        </div>
        <div className="flex flex-col gap-1.5">
          <Label className={labelClassName}>
            {labels?.apartment ?? "Apartment, building, suite"}
          </Label>
          <Input
            value={value.apartment}
            onChange={(e) => update("apartment", e.target.value)}
            placeholder="Optional"
          />
        </div>
      </div>

      <div
        className={cn(
          "grid grid-cols-1 gap-x-4 gap-y-6",
          equalColumns ? "sm:grid-cols-3" : "sm:grid-cols-[1fr_0.6fr_0.6fr]",
        )}
      >
        <div className="flex flex-col gap-1.5">
          <Label className={labelClassName} required={showRequired}>
            City
          </Label>
          <Input
            value={value.city}
            onChange={(e) => update("city", e.target.value)}
            placeholder="City"
            className={errorClass("city")}
          />
          {!hideErrorMessages && <FieldError message={errors?.city} className="mt-1" />}
        </div>
        <div className="flex flex-col gap-1.5">
          <Label className={labelClassName} required={showRequired}>
            State
          </Label>
          <Select value={value.state} onValueChange={(val) => update("state", val)}>
            <SelectTrigger className={errorClass("state")}>
              <SelectValue placeholder="State" />
            </SelectTrigger>
            <SelectContent>
              {US_STATES.map((s) => (
                <SelectItem key={s.value} value={getStateOption(s)}>
                  {getStateOption(s)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {!hideErrorMessages && <FieldError message={errors?.state} className="mt-1" />}
        </div>
        <div className="flex flex-col gap-1.5">
          <Label className={labelClassName} required={showRequired}>
            Zip Code
          </Label>
          <Input
            inputMode="numeric"
            maxLength={5}
            value={value.zipCode}
            onChange={(e) => update("zipCode", e.target.value.replace(/\D/g, "").slice(0, 5))}
            placeholder="12345"
            className={errorClass("zipCode")}
          />
          {!hideErrorMessages && <FieldError message={errors?.zipCode} className="mt-1" />}
        </div>
      </div>
    </div>
  );
};
